import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Check } from 'lucide-react';
import VideoPlaceholder from '../components/VideoPlaceholder';
import { HUBSPOT_800_SESSION } from '../lib/links';

gsap.registerPlugin(ScrollTrigger);

const roadmapItems = [
    'Which 3 integrations matter most for your specific workflow',
    'What your agent does on Day 1, Day 30, and Day 90',
    'Where AI saves you the most time (and where it won\u2019t)',
    'What your agent\u2019s identity looks like — its name, its role, its personality',
    'A realistic picture of what the build process looks like for your business',
];

const ReadinessPage = () => {
    const rootRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.utils.toArray('.readiness-section').forEach((section) => {
                gsap.from(section.querySelectorAll('.readiness-element'), {
                    y: 40,
                    opacity: 0,
                    duration: 1,
                    stagger: 0.08,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: section, start: 'top 80%' },
                });
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="bg-[#0A0A14] text-[#F0EFF4]">
            {/* Video — full width, above the fold */}
            <div className="pt-24">
                <VideoPlaceholder
                    label="VITO_VIDEO_1"
                    caption="Vito walks you through what the session covers. 90 seconds."
                />
            </div>

            {/* Headline */}
            <section className="readiness-section py-20 md:py-28 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="readiness-element text-[#F0EFF4] font-bold text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6">
                        You Already Know AI Is Coming for Your Industry.
                    </h1>
                    <p className="readiness-element font-drama italic text-[#7B61FF] text-2xl md:text-3xl leading-snug">
                        The only question is whether you&apos;re the one running it — or the one running from it.
                    </p>
                </div>
            </section>

            {/* Section 1 — The Label (Past Pain) */}
            <section className="readiness-section py-20 md:py-24 px-6 border-t border-[#7B61FF]/15">
                <div className="max-w-3xl mx-auto">
                    <p className="readiness-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-8">
                        Sound Familiar?
                    </p>
                    <div className="space-y-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                        <p className="readiness-element">
                            You&apos;re good at roofing. Maybe great. But you&apos;re doing the job of five people.
                        </p>
                        <p className="readiness-element">
                            Estimating. Following up on leads that go cold while you&apos;re on a roof. Scheduling crews. Chasing down missing paperwork. Answering the same questions from homeowners at 9 PM because nobody else will.
                        </p>
                        <p className="readiness-element">
                            You&apos;ve looked at AI tools. Maybe tried a chatbot. Maybe paid for software that promised to automate your follow-up and did... almost nothing.
                        </p>
                        <p className="readiness-element">
                            And somewhere in the back of your mind, you&apos;ve wondered: <em className="font-drama text-[#F0EFF4]">is there actually something here, or is this all just hype?</em>
                        </p>
                        <p className="readiness-element">
                            There&apos;s something here. But most of what&apos;s being sold to contractors right now is noise.
                        </p>
                        <p className="readiness-element font-bold text-[#F0EFF4]">
                            RoofClaw is not noise.
                        </p>
                    </div>
                </div>
            </section>

            {/* Section 2 — Sell the Vacation */}
            <section className="readiness-section py-20 md:py-24 px-6 bg-[#18181B] border-y border-[#7B61FF]/15">
                <div className="max-w-3xl mx-auto">
                    <p className="readiness-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-8">
                        What Your Business Looks Like With a RoofClaw Agent
                    </p>
                    <div className="space-y-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                        <p className="readiness-element">Imagine this.</p>
                        <p className="readiness-element">
                            It&apos;s 7 AM. You&apos;re drinking your coffee. Your agent has already followed up with every open estimate from yesterday. It pulled the weather forecast for your three installs this week and flagged a potential delay on Thursday. It drafted responses to the two leads that came in overnight — in your voice, with your company&apos;s talking points.
                        </p>
                        <p className="readiness-element">
                            You review. You approve. You make the calls that only you can make.
                        </p>
                        <p className="readiness-element font-bold text-[#F0EFF4]">
                            Everything else is handled.
                        </p>
                        <p className="readiness-element">
                            That&apos;s not a fantasy. That&apos;s what contractors running RoofClaw are doing right now.
                        </p>
                        <p className="readiness-element">
                            The question isn&apos;t whether this is real. The question is whether it&apos;s right for <em className="font-drama text-[#F0EFF4]">your</em> business, <em className="font-drama text-[#F0EFF4]">your</em> software stack, and <em className="font-drama text-[#F0EFF4]">your</em> team — right now.
                        </p>
                        <p className="readiness-element">
                            That&apos;s exactly what the AI Readiness Session answers.
                        </p>
                    </div>
                </div>
            </section>

            {/* Section 3 — The Offer */}
            <section className="readiness-section py-20 md:py-24 px-6">
                <div className="max-w-3xl mx-auto">
                    <p className="readiness-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-3">
                        The AI Readiness Session
                    </p>
                    <p className="readiness-element font-drama text-[#F0EFF4] text-6xl md:text-7xl leading-none mb-2">
                        $800 <span className="font-data text-[#F0EFF4]/40 text-lg align-middle tracking-widest">USD</span>
                    </p>
                    <h2 className="readiness-element text-[#F0EFF4] font-bold text-3xl md:text-4xl tracking-tight mt-8 mb-6">
                        One Hour. Your Roadmap. Yours to Keep.
                    </h2>
                    <div className="readiness-element space-y-5 text-[#F0EFF4]/80 text-lg leading-relaxed mb-10">
                        <p>
                            One hour with Adam Sand — founder of RBP Consulting, builder of RoofClaw, and the person who has sat across from more roofing business owners making AI decisions than anyone else in this industry.
                        </p>
                        <p>
                            We look at your business together. Your software. Your team. Your biggest operational bottlenecks. And we build your <strong className="text-[#F0EFF4]">personalized RoofClaw Implementation Roadmap</strong> — a clear, step-by-step plan for exactly how AI fits your operation.
                        </p>
                    </div>

                    <div className="readiness-element rounded-[2rem] border border-[#7B61FF]/30 bg-[#18181B] p-8 mb-8">
                        <p className="font-data text-[#F0EFF4]/40 text-xs tracking-widest uppercase mb-5">
                            What your roadmap covers
                        </p>
                        <ul className="flex flex-col gap-4">
                            {roadmapItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <Check size={18} className="text-[#7B61FF] shrink-0 mt-[3px]" />
                                    <span className="text-[#F0EFF4]/80 text-base leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <p className="readiness-element text-[#F0EFF4]/80 text-base md:text-lg leading-relaxed">
                        <strong className="text-[#F0EFF4]">This isn&apos;t a demo. This isn&apos;t a sales call.</strong> It&apos;s a working session. You&apos;ll walk away with a document — your roadmap — regardless of what you decide next.
                    </p>
                </div>
            </section>

            {/* Section 4 — Handle the Objection */}
            <section className="readiness-section py-20 md:py-24 px-6 bg-[#18181B] border-y border-[#7B61FF]/15">
                <div className="max-w-3xl mx-auto">
                    <p className="readiness-element font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-6">
                        &ldquo;Is This Just a Sales Call in Disguise?&rdquo;
                    </p>
                    <div className="space-y-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                        <p className="readiness-element">Fair question. Here&apos;s the honest answer.</p>
                        <p className="readiness-element">
                            Adam is going to show you exactly what RoofClaw can do for your business. If it&apos;s a fit, he&apos;ll tell you. If it&apos;s not — if your software doesn&apos;t have an API, if your team isn&apos;t ready, if the timing is wrong — he&apos;ll tell you that too.
                        </p>
                        <p className="readiness-element">
                            We don&apos;t build machines for contractors who aren&apos;t ready. It wastes everyone&apos;s time and it makes us look bad.
                        </p>
                        <p className="readiness-element">
                            The $800 exists because your time is worth something. So is Adam&apos;s. A real working session with a real deliverable isn&apos;t free — and it shouldn&apos;t be.
                        </p>
                        <p className="readiness-element rounded-2xl border border-[#7B61FF]/30 bg-[#7B61FF]/5 p-5 text-[#F0EFF4]">
                            <strong>And if you decide to move forward?</strong> The $800 you invest today applies directly to your $10,000 build price. You&apos;ll receive a private link to complete your purchase at $9,200.
                        </p>
                    </div>
                </div>
            </section>

            {/* Section 5 — Booking CTA */}
            <section className="readiness-section py-24 md:py-32 px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="readiness-element text-[#F0EFF4] font-bold text-4xl md:text-5xl tracking-tight mb-4">
                        Ready to <span className="font-drama italic text-[#7B61FF] font-normal">Build Your Roadmap?</span>
                    </h2>
                    <p className="readiness-element text-[#F0EFF4]/60 text-base md:text-lg mb-10">
                        60 minutes. Booked instantly below. Your Implementation Roadmap delivered same day.
                    </p>
                    <div className="readiness-element">
                        <a
                            href={HUBSPOT_800_SESSION}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-magnetic inline-flex items-center justify-center gap-3 bg-[#7B61FF] text-[#F0EFF4] px-10 py-5 rounded-full font-bold text-lg tracking-wide shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.7)] transition-shadow w-full sm:w-auto"
                        >
                            Book Your AI Readiness Session — $800 <ArrowRight size={20} />
                        </a>
                    </div>
                    <p className="readiness-element text-[#F0EFF4]/50 text-xs mt-6 font-data tracking-wide">
                        Secure checkout via HubSpot Payments. Questions? Email{' '}
                        <a href="mailto:vito@roofclaw.com" className="text-[#F0EFF4]/70 hover:text-[#7B61FF] transition-colors">
                            vito@roofclaw.com
                        </a>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default ReadinessPage;
